/**
 * @module app.cm
 */

import { autoinject, bindable, singleton } from 'aurelia-framework';
import { ComponentBase, StandardLoadOptions, Sorting, State, SortDirection } from 'app/shared/lib/essette-site';
import { UserService } from 'app/core/services/user-service';
import { CaseService } from 'app/cm/services/case-service';
import { QueueService } from 'app/core/services/queue-service';
import { IQueuesConfig } from 'app/core/models/essette-interfaces';
import { AppointmentService, IAppointmentSearchFilters } from 'app/cm/services/appointment-service';
import { apiCall } from 'app/shared/lib/essette-api';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { ICollapse } from 'app/shared/attributes/ess-collapse-attributes';
import { DateValue } from 'app/shared/lib/date-time';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import { guid } from 'app/shared/lib/guid';
import { EventAggregator } from 'aurelia-event-aggregator';
import { SetFocusEvent } from 'app/shared/lib/events';
import { isNullOrUndefined } from 'app/shared/lib/utilities';
import { I18N } from 'aurelia-i18n';
import { requiredFields as memberRequiredFields } from 'app/cd/elements/members/member-display';
import * as Core from 'app/core/models/essette-api';
import * as CM from 'app/cm/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

@singleton()
@autoinject()
export class AppointmentsPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    private appointmentResults = new Array<CM.TaskAppointment>();
    private searchFilters: IAppointmentSearchFilters | null = null;
    private tempSearchFilters: IAppointmentSearchFilters | null = null;

    @bindable
    public queue: Core.Queue | null = null;

    @bindable
    public delegatedTo: CD.Provider | Core.User | null = null;

    // Permissions
    protected hasCaseOwner: boolean = false;
    protected hasCareTeam: boolean = false;
    protected hasCaseRoute: boolean = false;
    protected showCaseSummaryLink: boolean = false;
    protected openCaseSummary = openCaseSummary;

    //lookup objects
    private taskTypes: CM.TaskTargetType[] = [];
    private caseQueues: CM.CaseQueue[] = [];
    private users: Core.User[] = [];
    protected queueProviders: Array<CD.Provider> | null = null;
    private queueUsers: Core.User[] = [];
    protected delegateToState: State = new State();
    private owners: CM.CaseOwner[] = [];

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 20;

    protected id: guid = guid();

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $collapsible!: ICollapse;
    protected $searchCriteriaHeader!: HTMLElement;
    protected $currentCriteriaHeader!: HTMLElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly userService: UserService,
        private readonly appointmentService: AppointmentService,
        private readonly caseService: CaseService,
        private readonly queueService: QueueService,
        private readonly events: EventAggregator,
        protected i18n: I18N,
        ...rest: any[]) {

        super(...rest);

        const queuesConfig: IQueuesConfig = {
            serviceRootUrl: 'cm/tasks'
        };

        this.queueService.configure(queuesConfig);
    }

    protected async canActivate(): Promise<boolean> {
        await this.checkPermissions();
        return true;
    }

    protected async activate(): Promise<void> {
        await this.loadLookups();

        // Only load provider list if at least one queue is a provider delegate type
        if(this.caseQueues.some((q) => q.delegated?.type?.code === 'CD.Provider')) {
            this.loadQueueProviders();
        }

        if(!this.tempSearchFilters) {
            this.tempSearchFilters = {};
        }

        if(!this.searchFilters) {
            this.searchFilters = {};

            if(this.hasCaseOwner) {
                this.searchFilters.assignedTo = { id: this.env.user.id, $display: this.env.user.description };
            } else {
                this.delegatedTo = { id: this.env.user.id, $display: this.env.user.description };
            }

            this.searchFilters.dateStartTime = this.env.today;
            this.searchFilters.dateEndTime = (new DateValue(this.env.today)).add(2, 'weeks').toString();

            this.searchFilters.myCases = this.hasCaseOwner;
            this.searchFilters.delegatedToMe = !this.hasCaseOwner;

            this.tempSearchFilters = { ...this.searchFilters };
        }
    }

    protected bind(): void {
        this.submitSearch();
    }

    ////****************** Events ***************************************************////
    protected async applySort(event: SortChangeEvent): Promise<void> {
        if(!this.appointmentResults.length) return;
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.loadAppointments();
    }

    protected async interventionQueueChanged(newQueue: Core.Queue | null): Promise<void> {
        if(this.tempSearchFilters) {
            if(newQueue?.delegated?.type?.code !== 'CD.Provider') if(newQueue?.id) await this.loadQueueUsers(newQueue.id);

            // Clear current delegatedTo selection if not in the new queue users collection
            if(this.tempSearchFilters?.delegatedToUser && !this.queueUsers.some(user => user.id === this.tempSearchFilters!.delegatedToUser!.id) && !this.tempSearchFilters?.delegatedToMe) {
                this.tempSearchFilters.delegatedToUser = null;
                this.tempSearchFilters.delegatedToProvider = null;
            }
        }
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
        this.hasCaseOwner = this.accountContext.hasPermission('CM.Case.Duty.Owner');
        this.hasCareTeam = this.accountContext.hasRouteAccess('cm.case.careteam');
        this.showCaseSummaryLink = this.accountContext.hasRouteAccess('cm.case.summary');
    }

    public showAvailableCriteria(): void {
        this.$collapsible.open(0);
        this.$collapsible.close(1);
    }

    public showSelectedCriteria(): void {
        this.$collapsible.close(0);
        this.$collapsible.open(1);
    }

    private clear(returnFocus?: boolean): void {
        this.appointmentResults = [];
        this.resultCount = 0;
        this.searchFilters = {};
        this.tempSearchFilters = {};
        this.delegatedTo = null;
        this.showAvailableCriteria();
        if(returnFocus) this.events.publish(new SetFocusEvent(this.$searchCriteriaHeader));
    }

    protected async submitSearch(): Promise<void> {
        if (this.tempSearchFilters) {
            if(isNullOrUndefined(this.tempSearchFilters.dateEndTime)){
            this.tempSearchFilters.dateEndTime = this.tempSearchFilters.dateStartTime;
            }
        }
        this.searchFilters = { ...this.tempSearchFilters };

        if(this.tempSearchFilters?.delegatedToUser) {
            this.delegatedTo = { ...this.tempSearchFilters?.delegatedToUser };
        } else if(this.tempSearchFilters?.delegatedToProvider) {
            this.delegatedTo = { ...this.tempSearchFilters?.delegatedToProvider };
        } else if(!this.tempSearchFilters?.delegatedToMe) {
            this.delegatedTo = null;
        }
        this.events.publish(new SetFocusEvent(this.$currentCriteriaHeader));
        await this.loadAppointments();
    }

    protected toggleMyCases(value: boolean): void {
        if(!this.searchFilters) this.searchFilters = {};
        if(!this.tempSearchFilters) this.tempSearchFilters = {};

        this.tempSearchFilters.myCases = value;

        this.tempSearchFilters.assignedTo = value ? { id: this.env.user.id, $display: this.env.user.description } : undefined;
    }

    protected toggleDelegatedToMe(value: boolean): void {
        if(!this.searchFilters) this.searchFilters = {};
        if(!this.tempSearchFilters) this.tempSearchFilters = {};

        this.tempSearchFilters.delegatedToMe = value;

        this.tempSearchFilters.delegatedToUser = value ? { id: this.env.user.id, $display: this.env.user.description } : null;
    }

    protected toggleMyCareTeam(value: boolean): void {
        if(!this.searchFilters) this.searchFilters = {};
        if(!this.tempSearchFilters) this.tempSearchFilters = {};

        this.tempSearchFilters.myCareTeam = value;

        this.tempSearchFilters.careTeamParticipant = value ? { id: this.env.user.id, $display: this.env.user.description } : undefined;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadLookups(): Promise<void> {
        try {
            this.state.beginLoad();

            const options: StandardLoadOptions = { all: true };

            const [taskTypes, queues, owners, users] = await Promise.all([
                this.caseService.loadTaskTargetTypes(options, undefined, ['code']),
                this.caseService.loadTaskQueues(options, undefined, ['delegated.type.code', 'description']),
                this.caseService.loadCaseOwners(options, undefined, undefined, undefined, ['description']),
                this.userService.loadUsers(options, undefined, undefined, ['description'])
            ]);
            this.taskTypes = taskTypes.results;
            this.caseQueues = queues.results;
            this.owners = owners.results;
            this.users = users.results;

            for(const taskType of this.taskTypes) {
                if(taskType.code === 'CM.Case') taskType.$display = this.i18n.tr('core:task.$');
                else if(taskType.code === 'CM.CarePlanGoal') taskType.$display = this.i18n.tr('cm:carePlanIntervention.$');
            }
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadQueueUsers(queueId: string): Promise<void> {
        if(!!this.queueUsers) this.queueUsers.splice(0);

        try {
            this.delegateToState.beginLoad();
            const response = await this.queueService.loadQueueUsers(queueId, { all: true }, undefined, ['description']);
            this.queueUsers = response.results;

        } catch(error) {
            throw error;
        } finally {
            this.delegateToState.endLoad();
        }
    }

    @apiCall
    private async loadQueueProviders(): Promise<void> {
        try {
            this.delegateToState.beginLoad();
            const response = await this.queueService.loadPortalProviders('', { all: true }, undefined, ['description']);
            this.queueProviders = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.delegateToState.endLoad();
        }
    }

    @apiCall
    private async loadAppointments(): Promise<void> {
        const options: StandardLoadOptions = {
            includeInactive: false,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting
        };

        const includes = [
            'case',
            'case.assignedTo',
            'case.member',
            'task',
            'task.attachedTo.type'
        ];

        const fields = [
            'case.assignedTo.description',
            'case.isCheckedOut',                    // for case-checked-out-icon
            'case.member.eligibility.isCurrent',
            'case.number',
            'dateTimeRange.end',
            'dateTimeRange.start',
            'task.attachedTo.type.code',            // for task-intervention-icon
            'task.description',
            'task.isCompleted',
            'task.metadata.completed.timestamp',
            'text'
        ];

        memberRequiredFields.forEach(field => !fields.includes(`case.member.${field}`) ? fields.push(`case.member.${field}`) : null);

        try {
            this.state.beginLoad();
            const response = await this.appointmentService.search(this.searchFilters || {}, options, includes, fields);
            this.appointmentResults = response?.results || [];
            // copy case onto each task object to pass case.number to task-intervention-icon
            for(const appointment of this.appointmentResults) {
                if(!appointment.task) appointment.task = {};
                appointment.task.case = appointment.case;
            }
            this.resultCount = response?.count || 0;
            this.showSelectedCriteria();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
